/**
 * What people did to this client's work.
 *
 * **This screen records nothing.** Every line it shows has been written to the
 * audit trail since the feature that performs it shipped — money corrections,
 * exclusions, additions, verifications, source views, snapshot imports, filing
 * confirmations, period transitions. What was missing was anywhere to read it,
 * which is a different problem from not having it (owner ask, Q8 2026-09-10).
 *
 * **The detail is shown, not summarised away.** A reviewer's own note is the
 * only answer to "why did this row leave the return", and a log that prints
 * `overlay.row_excluded` without it has kept the shape of the event and thrown
 * away its content. Reason codes make the question countable; the note answers
 * it on this row.
 *
 * Paging is keyset on `at`, because an audit trail grows at the head: an offset
 * page two shifts under the reader every time anything happens.
 */

import { Alert, Button, Card } from '@fiveox/ui'
import { History, RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import { CLIENT, useEvaluation } from '../lib/access'
import type { ActivityEvent } from '../lib/api'
import { clients, getOrgId } from '../lib/api'
import { useScopedClient } from '../lib/clientScope'
import { type Member, actorWords, fetchMembers } from '../lib/people'
import { session } from '../lib/session'
import { whenWords } from '../lib/when'

/*
 * **The words are the service's** (`app/activity.py`), sent with every event as
 * `title` and `lines`. They were a table here, and it drifted from the service
 * twice: on 2026-09-29 it named 13 of the 39 actions the service writes, and
 * printed every detail as recorded — keys, JSON, content hashes, match UUIDs.
 * The service's list is held total over what it writes by
 * `tests/test_activity_words.py`, which a table in this file could never be.
 */

/** The action filter's choices: each action seen, by its words, in the order they read. */
export function actionChoices(events: ActivityEvent[]): Array<{ action: string; title: string }> {
  const titles = new Map<string, string>()
  for (const e of events) titles.set(e.action, e.title)
  return [...titles.entries()]
    .map(([action, title]) => ({ action, title }))
    .sort((a, b) => a.title.localeCompare(b.title))
}

export function Activity() {
  const { client } = useScopedClient()
  const { can } = useEvaluation()
  const [events, setEvents] = useState<ActivityEvent[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // N32: who each actor is, from the platform's member list, read once and kept nowhere.
  const [members, setMembers] = useState<Member[] | null>(null)
  const me = session.useSession()?.userId ?? null

  useEffect(() => {
    let live = true
    void fetchMembers(getOrgId()).then((found) => {
      if (live) setMembers(found)
    })
    return () => {
      live = false
    }
  }, [])

  const load = useCallback(
    async (before?: string) => {
      if (!client) return
      setBusy(true)
      setError(null)
      try {
        const { data: body } = await clients.activity(client.id, {
          limit: 100,
          ...(before ? { before } : {}),
          ...(filter ? { action: filter } : {}),
        })
        // Appending on a cursor, replacing on a fresh read: the same call does
        // both, and getting it backwards silently duplicates the page.
        setEvents((prev) => (before ? [...prev, ...body.events] : body.events))
        setCursor(body.next_before)
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
      } finally {
        setBusy(false)
      }
    },
    [client, filter],
  )

  useEffect(() => {
    void load()
  }, [load])

  if (!can(CLIENT, 'can_view')) {
    return <div className="gst-empty">You cannot view this client.</div>
  }


  return (
    <div>
      <header className="mb-6">
        <h1 className="gst-h1">
          <History size={20} /> Activity
        </h1>
        <p className="gst-sub">
          Everything people did to this client&rsquo;s work, newest first &mdash; who,
          when, and in their own words
        </p>
      </header>

      {error && (
        <Alert tone="error" className="mb-4">
          {error}
        </Alert>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="gst-select"
          aria-label="Filter by action"
        >
          <option value="">Every action</option>
          {actionChoices(events).map(({ action, title }) => (
            <option key={action} value={action}>
              {title}
            </option>
          ))}
        </select>
        <Button type="button" variant="outline" size="sm" onClick={() => void load()}>
          <RefreshCw size={13} /> Refresh
        </Button>
      </div>

      {events.length === 0 && !busy ? (
        <div className="gst-empty">
          Nothing recorded for this client yet. Uploads, corrections, removals and
          approvals all appear here as they happen.
        </div>
      ) : (
        <ol className="space-y-2">
          {events.map((e) => (
            <li key={e.id}>
              <Card className="p-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-sm font-medium text-foreground">
                    {e.title}
                  </span>
                  <span className="gst-meta">
                    {whenWords(e.at)}
                    {' · '}
                    {/*
                      * A name from the platform's member list, never the id
                      * (N32). This service still holds no user directory —
                      * that would be the shadow copy constitution §1 forbids —
                      * so the list is read at render time and kept nowhere.
                      */}
                    {actorWords(e, members, me)}
                  </span>
                </div>
                {e.lines.length > 0 && (
                  <dl className="mt-2 grid gap-x-4 gap-y-1 sm:grid-cols-[max-content_1fr]">
                    {e.lines.map(({ label, value }, i) => (
                      <div key={`${label}:${i}`} className="contents">
                        <dt className="gst-meta">{label}</dt>
                        <dd className="break-words text-sm">{value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </Card>
            </li>
          ))}
        </ol>
      )}

      {cursor && (
        <div className="mt-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => void load(cursor)}
          >
            {busy ? 'Loading…' : 'Older'}
          </Button>
        </div>
      )}
    </div>
  )
}
